// src/pages/Dashboard/sections/network-tabs/FeesTab.jsx
import React, { useState, useEffect } from 'react';

/**
 * Network Stability Fees Tab Component
 * Displays network stability fees history and summary data
 * Enhanced to support viewing fees by node
 */
const FeesTab = ({
  feesData,
  feePeriod,
  setFeePeriod,
  formatNumber,
  formatWalletAddress,
  formatDate
}) => {
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [displayFees, setDisplayFees] = useState([]);
  const [nodeStabilityFees, setNodeStabilityFees] = useState({});
  const [selectedNodeName, setSelectedNodeName] = useState('All Nodes');
  
  // Helper function to format date with hours
  const formatDateWithHours = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return formatDate ? 
      formatDate(dateString, true) : // Use formatDate with hours if available
      date.toLocaleString(); // Fallback to toLocaleString which includes hours
  };
  
  // Helper function to determine if a fee is from self-referral
  const isSelfReferral = (fee) => {
    if (!fee) return false;
    return fee.is_self_referral || (fee.source_wallet && fee.recipient_wallet && 
           fee.source_wallet.toLowerCase() === fee.recipient_wallet.toLowerCase());
  };
  
  // Extract from fees data when component mounts or data changes
  useEffect(() => {
    if (!feesData) {
      setDisplayFees([]);
      return;
    }
    
    // If there's no active node filter, show all fees
    if (!activeNodeId) {
      setDisplayFees(feesData.fees || []);
      setNodeStabilityFees(feesData.summary || {});
      return;
    }
    
    // Filter fees for the selected node
    const nodeFees = (feesData.fees || []).filter(fee => {
      if (!fee.subordinate_node) return false;
      return fee.subordinate_node.id === activeNodeId || 
             fee.subordinate_node.reference_code === activeNodeId;
    });
    
    setDisplayFees(nodeFees);
    
    // Calculate totals for the selected node
    const totalAmount = nodeFees.reduce((sum, fee) => sum + fee.amount, 0);
    const selfReferralFees = nodeFees.filter(isSelfReferral);
    const selfReferralAmount = selfReferralFees.reduce((sum, fee) => sum + fee.amount, 0);
    
    setNodeStabilityFees({
      total_amount: totalAmount,
      total_count: nodeFees.length,
      self_referral_amount: selfReferralAmount,
      self_referral_count: selfReferralFees.length,
      external_fees_amount: totalAmount - selfReferralAmount,
      last_updated: nodeFees.length > 0 ? 
        nodeFees.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0].created_at : 
        null
    });
  }, [feesData, activeNodeId]);
  
  // Calculate summary values
  const totalFees = nodeStabilityFees.total_amount !== undefined ?
    nodeStabilityFees.total_amount :
    displayFees.reduce((sum, entry) => sum + (entry.amount || 0), 0);
  
  const selfReferralAmount = nodeStabilityFees.self_referral_amount !== undefined ?
    nodeStabilityFees.self_referral_amount :
    displayFees.filter(isSelfReferral).reduce((sum, entry) => sum + (entry.amount || 0), 0);
  
  const externalFeesAmount = nodeStabilityFees.external_fees_amount !== undefined ?
    nodeStabilityFees.external_fees_amount :
    totalFees - selfReferralAmount;
  
  // Calculate average fee
  const averageFee = displayFees.length > 0 ?
    totalFees / displayFees.length :
    0;
  
  // Calculate highest fee
  const highestFee = displayFees.length > 0 ?
    Math.max(...displayFees.map(entry => entry.amount || 0)) :
    0;
  
  return (
    <div className="fees-content">
      <div className="points-controls">
        <h3>Network Stability Fees</h3>
        <div className="filters-container">
          <div className="period-selector">
            <label>Time Period:</label>
            <select 
              value={feePeriod} 
              onChange={(e) => setFeePeriod(e.target.value)}
            >
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
              <option value="90d">90 Days</option>
              <option value="1y">1 Year</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
      </div>
      
      {displayFees.length > 0 ? (
        <>
          <div className="fees-summary">
            <div className="summary-card">
              <div className="summary-value">
                {formatNumber(totalFees, 2)}
              </div>
              <div className="summary-label">
                Total Network Stability Fees
              </div>
              <div className="summary-timestamp">
                Last Updated: {formatDateWithHours(nodeStabilityFees.last_updated)}
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-value self-referral-value">
                {formatNumber(selfReferralAmount, 2)}
              </div>
              <div className="summary-label">
                Self-Generated Fees
              </div>
              <div className="summary-info">
                From orders on your own node
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-value external-fees-value">
                {formatNumber(externalFeesAmount, 2)}
              </div>
              <div className="summary-label">
                Network-Generated Fees
              </div>
              <div className="summary-info">
                From your network's orders
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-value">
                {formatNumber(averageFee, 2)}
              </div>
              <div className="summary-label">Average Fee</div>
            </div>
            
            <div className="summary-card">
              <div className="summary-value">
                {formatNumber(highestFee, 2)}
              </div>
              <div className="summary-label">Highest Fee</div>
            </div>
            
            <div className="summary-card">
              <div className="summary-value">
                {feesData.summary?.total_records || displayFees.length}
              </div>
              <div className="summary-label">Total Transactions</div>
            </div>
          </div>
          
          <div className="detailed-fees-section">
            <h3>Fee Transaction History</h3>
            <div className="table-responsive">
              <table className="fees-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Source</th>
                    <th>Type</th>
                    <th>Source Node</th>
                    <th>Source Wallet</th>
                  </tr>
                </thead>
                <tbody>
                  {displayFees.map((fee, index) => {
                    const isSelfFee = isSelfReferral(fee);
                    return (
                      <tr key={index} className={isSelfFee ? 'self-referral-row' : ''}>
                        <td className="date-col">{formatDateWithHours(fee.created_at)}</td>
                        <td className="value-col">{formatNumber(fee.amount, 2)}</td>
                        <td className={`source-col ${isSelfFee ? 'self-referral' : ''}`}>
                          {isSelfFee ? 'Self-generated' : 'Network'}
                        </td>
                        <td>{fee.distribution_type || 'Network Stability'}</td>
                        <td>
                          {fee.subordinate_node?.reference_code || 
                           (fee.source_order?.selected_node?.reference_code || 'N/A')}
                        </td>
                        <td className="wallet-col">{formatWalletAddress(fee.source_wallet || '')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="no-data-message">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p>
            No network stability fees available for the selected period.
          </p>
          <p className="fees-explanation">
            Network stability fees are generated from completed orders in your network. 
            They include both fees from your own orders (self-generated) and from orders placed by nodes in your network.
          </p>
        </div>
      )}
      
      {/* Add additional styling for the fees tab */}
      <style jsx>{`
        .fees-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        
        .summary-card {
          background: var(--card-bg, #fff);
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          position: relative;
        }
        
        .summary-value {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 8px;
        }
        
        .self-referral-value {
          color: #10b981;
        }
        
        .external-fees-value {
          color: #6366f1;
        }
        
        .summary-label {
          font-size: 14px;
          color: var(--text-secondary, #6b7280);
          margin-bottom: 4px;
        }
        
        .summary-info {
          font-size: 12px;
          color: var(--text-tertiary, #9ca3af);
        }
        
        .summary-timestamp {
          font-size: 12px;
          color: var(--text-tertiary, #9ca3af);
          margin-top: 8px;
        }
        
        .date-col {
          min-width: 160px;
          white-space: nowrap;
        }
        
        .source-col {
          min-width: 120px;
        }
        
        .self-referral {
          color: #10b981;
          font-weight: 500;
        }
        
        .self-referral-row {
          background-color: rgba(16, 185, 129, 0.05);
        }
        
        .fees-explanation {
          max-width: 500px;
          margin: 16px auto;
          font-size: 14px;
          color: var(--text-secondary, #6b7280);
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
};

export default FeesTab;
